import { ButtonLoading } from '@/components/ui/button';
import * as Yup from 'yup';
import { Form, Formik } from 'formik';
import { useAlert } from '@/hooks/useAlert';
import { useAuth } from '@/hooks/useAuth';
import { useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import type { LoginUser, LoginValues } from '@/graphql/types/user';
import { MUTATION_LOGIN_USER } from '@/graphql/mutation/user';
import { REMEMBER_ME_KEY } from '@/context/auth';
import { AppRouters } from '@/utils/constants/routers';
import { Alert } from '@/components/molecules/alert';
import { PublicAdminRoute } from '@/components/organisms/route/admin';
import { InputWithLabel } from '@/components/ui/input';

export default function AdminPage() {
  const apolloClient = useApolloClient();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const { alert, showAlert, handleCloseAlert } = useAlert();
  const { getUser, getToken } = useAuth();

  const validationSchema = Yup.object().shape({
    email: Yup.string().email().required('Email is required'),
    password: Yup.string().required('Password is required'),
  });

  const initialValues = {
    email: '',
    password: '',
    rememberMe: false,
  };

  const loginUser = async (values: LoginValues) => {
    const { data } = await apolloClient.mutate<{ loginUser: LoginUser }>({
      mutation: MUTATION_LOGIN_USER,
      variables: {
        input: {
          email: values.email,
          password: values.password,
        },
      },
    });

    if (data) {
      getUser(data.loginUser.user);
      getToken(data.loginUser.access_token);
    }
  };

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      setLoading(true);
      localStorage.setItem(REMEMBER_ME_KEY, String(values.rememberMe));
      await loginUser({ email: values.email, password: values.password });
      navigate(AppRouters.translatePanel);
    } catch (error) {
      showAlert('error', String(error).split('Error: ')[1]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicAdminRoute>
      <div className="flex">
        {/* Image side */}
        <div className="flex-1 not-md:hidden">
          <img
            className="w-full h-dvh object-cover"
            src="/img/png/admin-bg.png"
            alt="Admin Background"
          />
        </div>

        {/* Form side */}
        <div className="flex-1 flex justify-center items-center h-dvh flex-col gap-6 p-4">
          {/* Logo */}
          <img
            className="w-full max-w-[136px] h-auto object-cover"
            src="/img/png/logos/logo_kyt.png"
            alt="Logo KYT"
          />

          {/* Card */}
          <div className="bg-white border border-slate-200 rounded-3xl shadow p-6 flex flex-col gap-6 justify-center items-center">
            {/* Title and subtitle */}
            <div className="flex flex-col gap-4 text-center">
              <h3 className="text-2xl font-semibold text-slate-900">Login</h3>
              <p className="text-sm text-slate-500">
                Enter your email below to login to your app account.
              </p>
            </div>

            <Formik
              initialValues={initialValues}
              onSubmit={handleSubmit}
              validationSchema={validationSchema}
            >
              {({ errors, handleChange }) => (
                <Form className="flex flex-col gap-6 w-full max-w-[350px]">
                  <div className="flex flex-col gap-4">
                    <InputWithLabel
                      label="E-mail"
                      type="email"
                      id="email"
                      name="email"
                      onChange={handleChange}
                      error={Boolean(errors.email)}
                      helperText={errors.email}
                    />

                    <div className="flex flex-col gap-2">
                      <InputWithLabel
                        label="Senha"
                        type="password"
                        id="password"
                        name="password"
                        onChange={handleChange}
                        error={Boolean(errors.password)}
                        helperText={errors.password}
                      />

                      <a className="text-sm text-gray-500" href="#">
                        Forgot your password?
                      </a>
                    </div>
                  </div>

                  <ButtonLoading
                    variant="default"
                    type="submit"
                    size="xl"
                    className="bg-slate-900 text-white"
                    label="Login"
                    loading={loading}
                    disabled={loading}
                  />
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>

      <Alert
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
      />
    </PublicAdminRoute>
  );
}
